import { useState } from "react";
import { OpenService } from "../_service/open.service";

// versioned key: "meta" (v1) held entries poisoned by the broken external
// scraper ("Something went wrong" payloads); bumping the key discards them
const META_STORAGE_KEY = "meta-v2";
// cached entries (successes and failures) expire after a day
const META_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedMeta {
  data: LinkMeta | null;
  ts: number;
}

// one-time cleanup of the old poisoned cache
try {
  localStorage.removeItem("meta");
} catch (e) {}

function readMetaStorage(): Record<string, CachedMeta> {
  try {
    const raw = localStorage.getItem(META_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, CachedMeta>) : {};
  } catch (e) {
    return {};
  }
}

// drop the error payload the old scraper used to return, in case it ever
// reappears upstream — never render it as a preview
function isErrorPayload(meta?: LinkMeta | null): boolean {
  return !!meta && meta["og:title"] === "Something went wrong";
}

export const useMeta = (): {
  getMetaMap: (url?: string) => Promise<LinkMeta> | null;
} => {
  const [metaMap, setMetaMap] = useState<Record<string, LinkMeta>>({});

  const fetchData = async (url: string): Promise<LinkMeta | undefined> => {
    try {
      const response = await OpenService.getLinkMetaData(url);

      return response["result"];
    } catch (error) {
      return undefined;
    }
  };

  async function getMetaMap(url?: string): Promise<any> {
    if (!url) return Promise.resolve(null);

    // Check for the blacklist urls
    const isBlacklisted = blacklistUrls.some((blacklistUrl) =>
      url.includes(blacklistUrl)
    );

    if (isBlacklisted) {
      return Promise.resolve(null);
    }

    // Check for valid link
    const isValidLink = url.match(/^(ftp|http|https|www):\/\/[^ "]+$/);
    if (!isValidLink) {
      return Promise.resolve(null);
    }

    // Check for the map if the data is already present
    if (metaMap[url]) {
      return Promise.resolve(metaMap[url]);
    }

    // Check for a fresh entry in storage (covers cached failures too,
    // so a dead link is not re-fetched on every render until the TTL passes)
    const stored = readMetaStorage()[url];
    if (stored && Date.now() - stored.ts < META_CACHE_TTL_MS) {
      return Promise.resolve(isErrorPayload(stored.data) ? null : stored.data);
    }

    const meta = await fetchData(url);

    if (meta && !isErrorPayload(meta)) {
      updateLocalStorage(url, meta);
      return Promise.resolve(meta);
    }
    updateLocalStorage(url, null);

    return Promise.resolve(null);
  }

  function updateLocalStorage(url: string, meta: LinkMeta | null) {
    try {
      const storageMap = readMetaStorage();
      storageMap[url] = { data: meta, ts: Date.now() };
      localStorage.setItem(META_STORAGE_KEY, JSON.stringify(storageMap));
    } catch (e) {
      // storage full or unavailable — previews just won't be cached
    }
    if (meta) {
      setMetaMap((prev) => ({ ...prev, [url]: meta }));
    }
  }

  return { getMetaMap };
};

const blacklistUrls = [
  "www.google.com",
  "api.whatsapp.com",
  "api.pensil.in",
  "livemeeting.pro",
  "pensil-social.s3",
  "googleusercontent.com",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".bmp",
  ".svg",
  ".ico",
  ".tif",
  ".tiff",
  ".psd",
  ".ai",
  ".eps",
  ".doc",
  ".docx",
  ".pdf",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
  ".zip",
  ".rar",
  ".tar",
  ".gz",
  ".7z",
  ".mp3",
  ".mp4",
  ".avi",
  ".wmv",
  ".mov",
  ".mkv",
  ".flv",
  ".wav",
  ".ogg",
  ".ogv",
  ".oga",
  ".webm",
  ".m4a",
  ".m4v",
  ".mpg",
  ".mpeg",
  ".m2v",
  ".mpg",
  ".mpeg",
  ".m2v",
];

export interface LinkMeta {
  "application-name": string;
  date: string;
  description: string;
  keywords: string;
  "og:description": string;
  "og:image": string;
  "og:site_name": string;
  "og:title": string;
  "og:type": string;
  "og:url": string;
  title: string;
  "twitter:card": string;
  "twitter:creator": string;
  "twitter:description": string;
  "twitter:site": string;
  "twitter:title": string;
  url: string;
}
